import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Building2, Plus, Copy, LogIn, Power, Check, CreditCard, RefreshCw, PauseCircle, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import TenureModal from "../components/TenureModal";

// API may be a relative "/api" now, so build the shareable apply link off the
// current page origin (crm.wizardsoftware.in) instead of the API base.
const ORIGIN = typeof window !== "undefined" ? window.location.origin : API.replace(/\/api\/?$/, "");
const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])?$/;
const HEX = /^#[0-9a-fA-F]{6}$/;

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return "—"; }
};
const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// 🟢 > 30 days · 🟡 ≤ 30 days · 🔴 expired or in the grace window (still
// shown distinctly from a hard "Expired" so platform staff know there's a
// few days of runway before the tenant is actually blocked).
function expiryChip(t) {
  const dr = t.days_remaining;
  if (dr == null) return <Badge className="bg-slate-100 text-slate-500">—</Badge>;
  if (dr > 30) return <Badge className="bg-emerald-100 text-emerald-800">{dr}d left</Badge>;
  if (dr >= 0) return <Badge className="bg-amber-100 text-amber-800">{dr}d left</Badge>;
  const graceLeft = (t.grace_period_days || 0) + dr;
  return graceLeft > 0
    ? <Badge className="bg-red-100 text-red-800">Grace ({graceLeft}d)</Badge>
    : <Badge className="bg-red-100 text-red-800">Expired</Badge>;
}

export default function PlatformTenants() {
  const { enterImpersonation, user } = useAuth();
  // This whole page is already unreachable for anyone but a platform owner
  // (App.js's <Protected platform redirectTo="/applications">) — this is a
  // second, cheap check directly around the two most sensitive actions
  // (financial tenure/renewal, suspend/resume) specifically, so they stay
  // locked down even if the route guard were ever loosened without anyone
  // noticing this page still carries them. is_platform_admin is the real
  // field this app has (no role: "platform_owner" value exists).
  const isPlatformOwner = !!user?.is_platform_admin;
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wizard, setWizard] = useState(false);
  const [planTarget, setPlanTarget] = useState(null);
  const [tenureTarget, setTenureTarget] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/platform/tenants").then((r) => setRows(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail))).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  // Native confirm rather than a new modal component for a one-line
  // yes/no — this is the only spot in the whole page still using
  // t.is_active directly (matches the /toggle-status request body, which
  // is a status string, not the boolean the older PATCH .../status took).
  const toggleStatus = async (t) => {
    if (!isPlatformOwner) return; // the button is hidden too; this is the handler-level backstop
    const activating = t.status === "suspended";
    if (!activating) {
      if (!window.confirm(`Suspend ${t.name}? Their CRM users will be blocked immediately.`)) return;
    } else if (!window.confirm(`Reinstate ${t.name}'s access?`)) {
      return;
    }
    const reason = activating ? "" : (window.prompt("Reason for suspension (optional):", "Administrative suspension") || "");
    try {
      await api.post(`/platform/tenants/${t.id}/toggle-status`, {
        status: activating ? "active" : "suspended", reason,
      });
      toast.success(`${t.name} ${activating ? "reinstated" : "suspended"}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Seasonal Off-Season Hold — a different action from suspend/reinstate:
  // a paused tenant isn't overdue or administratively blocked, they're
  // deliberately parked (e.g. a college's off-season), and
  // subscription_jobs.py's daily evaluator credits their valid_until
  // forward for every day this stays true so their clock never runs down
  // while paused.
  const togglePause = async (t) => {
    if (!isPlatformOwner) return; // the button is hidden too; this is the handler-level backstop
    const pausing = !t.is_paused;
    if (pausing) {
      if (!window.confirm(`Put ${t.name} into Off-Season Hold? Their CRM users will be blocked until resumed, and their subscription clock stops while paused.`)) return;
    } else if (!window.confirm(`Resume ${t.name} from Off-Season Hold?`)) {
      return;
    }
    const reason = pausing ? (window.prompt("Reason for pause (optional):", "Seasonal off-cycle") || "") : "";
    try {
      await api.post(`/platform/tenants/${t.id}/pause`, { is_paused: pausing, reason });
      toast.success(`${t.name} ${pausing ? "put on Off-Season Hold" : "resumed"}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const switchTo = async (t) => {
    try {
      const { data } = await api.post(`/platform/tenants/${t.id}/impersonate`);
      await enterImpersonation({ token: data.access_token, institution: data.institution, acting_as: data.acting_as });
      toast.success(`Now viewing ${t.name}`);
      navigate("/analytics");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const copyApply = (t) => {
    navigator.clipboard.writeText(`${ORIGIN}/apply/${t.slug}`).then(() => toast.success("Apply link copied"));
  };

  return (
    <div data-testid="platform-tenants-page" className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <Building2 className="w-6 h-6" /> SaaS Control Panel
          </h1>
          <p className="text-sm text-slate-500 mt-1">Provision, inspect, and suspend client institutions.</p>
        </div>
        <Button className="bg-brand w-full sm:w-auto" data-testid="new-tenant-button" onClick={() => setWizard(true)}>
          <Plus className="w-4 h-4 mr-1.5" /> Provision tenant
        </Button>
      </div>

      <Card className="border-slate-200" data-testid="tenants-table">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                {["Institution", "Slug", "Domains", "Leads", "Apps", "Counsellors", "Seats", "Expires", "Status", "Created", ""].map((h) => (
                  <TableHead key={h} className="text-xs font-semibold uppercase tracking-wider text-slate-500">{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={11} className="text-center py-10 text-slate-400">Loading…</TableCell></TableRow>}
              {!loading && rows.map((t, i) => (
                <TableRow key={t.id} data-testid={`tenant-row-${i}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="w-6 h-6 rounded" style={{ background: t.primary_color }} />
                      <div>
                        <div className="font-medium text-slate-800">{t.name}</div>
                        <div className="text-xs text-slate-400 font-mono">{t.id}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{t.slug}</TableCell>
                  <TableCell className="text-xs text-slate-500">
                    {(t.domains || []).length ? t.domains.map((d) => <div key={d}>{d}</div>) : <span className="text-slate-300">—</span>}
                  </TableCell>
                  <TableCell className="font-mono">{t.total_leads}</TableCell>
                  <TableCell className="font-mono">{t.total_applications}</TableCell>
                  <TableCell className="font-mono">{t.counselor_count}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <span className={`font-mono text-xs ${(t.active_counselor_count ?? 0) >= (t.plan?.counselor_seat_limit ?? 5) ? "text-red-600 font-semibold" : "text-slate-600"}`}>
                        {t.active_counselor_count ?? 0} / {t.plan?.counselor_seat_limit ?? 5}
                      </span>
                      <Badge variant="secondary" className="text-[10px] capitalize">{t.plan?.tier || "starter"}</Badge>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-0.5" data-testid={`tenant-expires-${i}`}>
                      <span className="text-xs text-slate-600">{fmtDate(t.valid_until)}</span>
                      {expiryChip(t)}
                    </div>
                  </TableCell>
                  <TableCell>
                    {/* Derived status (routes_auth.is_tenant_suspended) — a
                        tenant that's still is_active but past valid_until +
                        grace shows Suspended here too, not just is_active. */}
                    <div className="flex flex-wrap items-center gap-1">
                      <Badge className={t.status === "suspended" ? "bg-red-100 text-red-800" : "bg-emerald-100 text-emerald-800"}
                        data-testid={`tenant-status-${i}`}>
                        {t.status === "suspended" ? "Suspended" : "Active"}
                      </Badge>
                      {t.is_paused && (
                        <Badge className="bg-blue-100 text-blue-800" title={t.pause_reason || "Off-Season Hold"}
                          data-testid={`tenant-paused-badge-${i}`}>
                          Off-Season Hold
                        </Badge>
                      )}
                      {t.over_grace_exempt && (
                        <Badge className="bg-amber-100 text-amber-800"
                          title="Exempt from auto-suspension — past grace but not blocked"
                          data-testid={`tenant-exempt-badge-${i}`}>
                          ⚠ Exempt (Overdue)
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-slate-400 font-mono">{(t.created_at || "").slice(0, 10)}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" title="Copy apply link" data-testid={`tenant-copy-${i}`} onClick={() => copyApply(t)}>
                        <Copy className="w-4 h-4 text-slate-500" />
                      </Button>
                      <Button variant="ghost" size="icon" title="Switch to tenant" data-testid={`tenant-switch-${i}`} onClick={() => switchTo(t)}>
                        <LogIn className="w-4 h-4 text-blue-600" />
                      </Button>
                      <Button variant="ghost" size="icon" title="Manage plan" data-testid={`tenant-plan-${i}`} onClick={() => setPlanTarget(t)}>
                        <CreditCard className="w-4 h-4 text-indigo-600" />
                      </Button>
                      {isPlatformOwner && (
                        <Button variant="ghost" size="icon" title="Renew / extend tenure" data-testid={`tenant-renew-${i}`} onClick={() => setTenureTarget(t)}>
                          <RefreshCw className="w-4 h-4 text-teal-600" />
                        </Button>
                      )}
                      {isPlatformOwner && (
                        <Button variant="ghost" size="icon" title={t.status === "suspended" ? "Reinstate" : "Suspend"} data-testid={`tenant-toggle-${i}`} onClick={() => toggleStatus(t)}>
                          <Power className={`w-4 h-4 ${t.status === "suspended" ? "text-emerald-600" : "text-red-500"}`} />
                        </Button>
                      )}
                      {isPlatformOwner && (
                        <Button variant="ghost" size="icon" title={t.is_paused ? "Resume from Off-Season Hold" : "Pause / Off-Season Hold"} data-testid={`tenant-pause-${i}`} onClick={() => togglePause(t)}>
                          {t.is_paused
                            ? <PlayCircle className="w-4 h-4 text-blue-600" />
                            : <PauseCircle className="w-4 h-4 text-amber-600" />}
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      {wizard && <OnboardWizard onClose={() => setWizard(false)} onDone={() => { setWizard(false); load(); }} />}
      {planTarget && (
        <PlanDialog tenant={planTarget} onClose={() => setPlanTarget(null)}
          onDone={() => { setPlanTarget(null); load(); }} />
      )}
      {isPlatformOwner && tenureTarget && (
        <TenureModal tenant={tenureTarget} onClose={() => setTenureTarget(null)}
          onDone={() => { setTenureTarget(null); load(); }} />
      )}
    </div>
  );
}

const PLAN_TIERS = ["starter", "growth", "scale", "enterprise"];
const ADDON_DURATIONS = [
  { v: "1", label: "1 Month" },
  { v: "2", label: "2 Months" },
  { v: "3", label: "3 Months" },
  { v: "custom", label: "Custom Date" },
];

function PlanDialog({ tenant, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    tier: tenant.plan?.tier || "starter",
    counselor_seat_limit: tenant.plan?.counselor_seat_limit ?? 5,
    price_per_additional_seat: tenant.plan?.price_per_additional_seat ?? 1500,
    max_daily_leads: tenant.plan?.max_daily_leads ?? 50,
    exempt: !!tenant.exempt_from_suspension,
    // Seat Validity Tenure
    additionalSeats: 0,
    tenureType: "fixed", // "fixed" | "temporary"
    duration: "1",       // "1" | "2" | "3" | "custom"
    customExpiry: "",
  });
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  const additionalSeats = Number(f.additionalSeats || 0);
  const isTemporary = f.tenureType === "temporary";
  const isCustomDuration = f.duration === "custom";

  // Client-side preview only — the server (routes_platform.update_tenant_plan)
  // recomputes this for real on submit, same "preview vs. authoritative"
  // split as TenureModal's renewal preview.
  const previewMonths = isCustomDuration
    ? (f.customExpiry ? Math.max(1, Math.round((new Date(`${f.customExpiry}T00:00:00Z`) - new Date()) / 86400000 / 30)) : 1)
    : Number(f.duration || 1);
  const seatSubtotal = additionalSeats * Number(f.price_per_additional_seat || 0) * previewMonths;
  const seatTotal = Math.round(seatSubtotal * 1.18 * 100) / 100;

  const maxDailyLeadsNum = Number(f.max_daily_leads);
  const maxDailyLeadsValid = (maxDailyLeadsNum === -1) || (maxDailyLeadsNum >= 1 && maxDailyLeadsNum <= 5000);
  const baseValid = PLAN_TIERS.includes(f.tier) && Number(f.counselor_seat_limit) >= 0
    && Number(f.price_per_additional_seat) >= 0 && maxDailyLeadsValid;
  const addonValid = additionalSeats <= 0 || !isTemporary || !isCustomDuration || !!f.customExpiry;
  const valid = baseValid && addonValid;

  const submit = async () => {
    setBusy(true);
    try {
      const body = {
        tier: f.tier,
        counselor_seat_limit: Number(f.counselor_seat_limit),
        price_per_additional_seat: Number(f.price_per_additional_seat),
        max_daily_leads: maxDailyLeadsNum,
        exempt_from_suspension: f.exempt,
      };
      if (additionalSeats > 0) {
        body.additional_seats = additionalSeats;
        body.seat_tenure_type = f.tenureType;
        if (isTemporary) {
          if (isCustomDuration) body.addon_custom_expiry = f.customExpiry;
          else body.addon_duration_months = Number(f.duration);
        }
      }
      const { data } = await api.patch(`/platform/tenants/${tenant.id}/plan`, body);
      toast.success(
        data.publishers_updated
          ? `${tenant.name}'s plan updated — daily quota pushed to ${data.publishers_updated} connector${data.publishers_updated === 1 ? "" : "s"}`
          : `${tenant.name}'s plan updated`
      );
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const seatHistory = (tenant.tenure_history || []).filter((h) => h.type === "SEAT_EXPANSION");

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="plan-dialog" className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Manage plan · {tenant.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-slate-500">
            Currently using {tenant.active_counselor_count ?? 0} of {tenant.plan?.counselor_seat_limit ?? 5} counselor seats.
          </p>
          <div><Label className="text-xs">Tier</Label>
            <select className="mt-1 w-full h-9 rounded-md border border-slate-200 bg-white px-3 text-sm capitalize"
              data-testid="plan-tier" value={f.tier} onChange={(e) => set("tier")(e.target.value)}>
              {PLAN_TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div><Label className="text-xs">Base counselor seat limit</Label>
            <Input className="mt-1 w-40" type="number" min={0} data-testid="plan-seat-limit"
              value={f.counselor_seat_limit} onChange={(e) => set("counselor_seat_limit")(e.target.value)} /></div>
          <div><Label className="text-xs">Price per additional seat (₹/mo)</Label>
            <Input className="mt-1 w-40" type="number" min={0} data-testid="plan-seat-price"
              value={f.price_per_additional_seat} onChange={(e) => set("price_per_additional_seat")(e.target.value)} /></div>
          <div><Label className="text-xs">Daily Lead Ingestion Limit</Label>
            <Input className="mt-1 w-40" type="number" min={-1} max={5000} data-testid="plan-max-daily-leads"
              value={f.max_daily_leads} onChange={(e) => set("max_daily_leads")(e.target.value)} />
            <p className="text-xs text-slate-400 mt-1">
              Max webhook leads/day across all of this tenant's connectors. Up to 5000, or -1 for unlimited.
              Applies instantly to every existing connector.
            </p>
          </div>
        </div>

        {/* Seat Validity Tenure */}
        <div className="space-y-2.5 pt-3 border-t border-slate-200">
          <Label className="text-xs">Add Seats</Label>
          <Input className="w-40" type="number" min={0} data-testid="plan-additional-seats"
            placeholder="0" value={f.additionalSeats} onChange={(e) => set("additionalSeats")(e.target.value)} />

          {additionalSeats > 0 && (
            <>
              <Label className="text-xs">Seat Validity Tenure</Label>
              <div className="flex flex-wrap gap-2">
                <button type="button" data-testid="plan-tenure-fixed" onClick={() => set("tenureType")("fixed")}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    !isTemporary ? "bg-brand text-white border-transparent" : "border-slate-200 text-slate-600 hover:bg-slate-50"
                  }`}>
                  Fixed to Base Contract
                </button>
                <button type="button" data-testid="plan-tenure-temporary" onClick={() => set("tenureType")("temporary")}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    isTemporary ? "bg-brand text-white border-transparent" : "border-slate-200 text-slate-600 hover:bg-slate-50"
                  }`}>
                  Temporary Add-on Period
                </button>
              </div>

              {isTemporary && (
                <div className="flex flex-wrap items-center gap-2">
                  <select className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm"
                    data-testid="plan-addon-duration" value={f.duration} onChange={(e) => set("duration")(e.target.value)}>
                    {ADDON_DURATIONS.map((d) => <option key={d.v} value={d.v}>{d.label}</option>)}
                  </select>
                  {isCustomDuration && (
                    <Input type="date" className="w-44 h-9 text-xs" data-testid="plan-addon-custom-date"
                      value={f.customExpiry} onChange={(e) => set("customExpiry")(e.target.value)} />
                  )}
                </div>
              )}

              {isTemporary ? (
                <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 flex items-center justify-between">
                  <span className="text-xs text-slate-500">
                    {additionalSeats} seat{additionalSeats === 1 ? "" : "s"} × {money(f.price_per_additional_seat)} × {previewMonths} mo (+18% GST)
                  </span>
                  <span className="font-mono font-semibold text-slate-900" data-testid="plan-seat-billing-total">{money(seatTotal)}</span>
                </div>
              ) : (
                <p className="text-xs text-slate-500">
                  Permanently raises the base contract to <b className="text-slate-800">{Number(f.counselor_seat_limit) + additionalSeats}</b> seats
                  — billed as part of the recurring plan, not a one-off add-on charge.
                </p>
              )}
            </>
          )}
        </div>

        {/* Auto-suspension exemption */}
        <div className="flex items-start gap-2 pt-3 border-t border-slate-200">
          <input type="checkbox" id="plan-exempt" checked={f.exempt} data-testid="plan-exempt-suspension"
            onChange={(e) => set("exempt")(e.target.checked)} className="h-4 w-4 mt-0.5" />
          <Label htmlFor="plan-exempt" className="font-normal cursor-pointer text-sm leading-snug">
            Exempt from Auto-Suspension
            <span className="block text-xs text-slate-400 font-normal">
              Allow grace period overage for Enterprise POs — access stays on past expiry; an amber audit badge
              flags it in the SaaS panel instead of a 403 block.
            </span>
          </Label>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button className="bg-brand" data-testid="plan-submit" disabled={busy || !valid} onClick={submit}>
            {busy ? "Saving…" : "Save plan"}
          </Button>
        </DialogFooter>

        {/* Seat change history — SEAT_EXPANSION entries from
            tenure_history; renewals show in the Renew modal instead. */}
        {seatHistory.length > 0 && (
          <div className="pt-3 border-t border-slate-200">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Seat Change History</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="plan-seat-history-table">
                <thead>
                  <tr className="text-left text-slate-400">
                    <th className="py-1.5 pr-3 font-medium">Date</th>
                    <th className="py-1.5 pr-3 font-medium">Seats</th>
                    <th className="py-1.5 pr-3 font-medium">Tenure</th>
                    <th className="py-1.5 pr-3 font-medium">New Limit</th>
                    <th className="py-1.5 pr-3 font-medium">Total (GST incl.)</th>
                    <th className="py-1.5 pr-3 font-medium">Recorded By</th>
                  </tr>
                </thead>
                <tbody>
                  {seatHistory.map((h) => (
                    <tr key={h.transaction_id} className="border-t border-slate-100">
                      <td className="py-1.5 pr-3 whitespace-nowrap text-slate-600">{fmtDate(h.created_at)}</td>
                      <td className="py-1.5 pr-3 font-mono">+{h.additional_seats}</td>
                      <td className="py-1.5 pr-3 capitalize">
                        {h.tenure_type === "temporary" ? `Temp · ${fmtDate(h.addon_valid_until)}` : "Fixed"}
                      </td>
                      <td className="py-1.5 pr-3 font-mono">{h.new_seat_limit}</td>
                      <td className="py-1.5 pr-3 font-mono">{h.total_amount_inr != null ? money(h.total_amount_inr) : "—"}</td>
                      <td className="py-1.5 pr-3 text-slate-500">{h.recorded_by || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const STEPS = ["Identity", "Branding", "Admin"];

function OnboardWizard({ onClose, onDone }) {
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    name: "", slug: "", domain: "",
    primary_color: "#1E40AF", accent_color: "#3B82F6", base_application_fee: 1500,
    initial_admin_name: "", initial_admin_email: "", initial_admin_password: "",
  });
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  const canNext = [
    f.name.trim().length >= 2 && SLUG_RE.test(f.slug.trim().toLowerCase()),
    HEX.test(f.primary_color) && HEX.test(f.accent_color) && Number(f.base_application_fee) >= 0,
    f.initial_admin_name.trim().length >= 2 && /\S+@\S+\.\S+/.test(f.initial_admin_email) && f.initial_admin_password.length >= 8,
  ];

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/platform/tenants", {
        name: f.name.trim(),
        slug: f.slug.trim().toLowerCase(),
        domains: f.domain.trim() ? [f.domain.trim().toLowerCase()] : [],
        primary_color: f.primary_color,
        accent_color: f.accent_color,
        base_application_fee: Number(f.base_application_fee) || 0,
        initial_admin_name: f.initial_admin_name.trim(),
        initial_admin_email: f.initial_admin_email.trim().toLowerCase(),
        initial_admin_password: f.initial_admin_password,
      });
      toast.success(
        `${data.name} provisioned. Login: ${data.admin_email} · Apply: ${window.location.origin}${data.apply_url}`,
        { duration: 12000 }
      );
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="onboard-wizard" className="max-w-lg">
        <DialogHeader><DialogTitle>Provision a new tenant · {STEPS[step]}</DialogTitle></DialogHeader>

        <div className="flex items-center gap-1.5 mb-2">
          {STEPS.map((s, i) => (
            <span key={s} className="h-1.5 flex-1 rounded-full" style={{ background: i <= step ? "var(--brand-primary)" : "#e2e8f0" }} />
          ))}
        </div>

        {step === 0 && (
          <div className="space-y-3">
            <div><Label className="text-xs">Institution name</Label>
              <Input className="mt-1" data-testid="wiz-name" value={f.name} onChange={(e) => set("name")(e.target.value)} placeholder="Zenith Business School" /></div>
            <div><Label className="text-xs">URL slug</Label>
              <Input className="mt-1 font-mono" data-testid="wiz-slug" value={f.slug}
                onChange={(e) => set("slug")(e.target.value.toLowerCase())} placeholder="zenith" />
              <p className="text-xs text-slate-400 mt-1">Public portal: {window.location.origin}/apply/{f.slug || "slug"}</p>
            </div>
            <div><Label className="text-xs">Primary custom domain (optional)</Label>
              <Input className="mt-1 font-mono" data-testid="wiz-domain" value={f.domain}
                onChange={(e) => set("domain")(e.target.value)} placeholder="zenith.wizardsoftware.in" /></div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-3">
            {[["primary_color", "Primary colour"], ["accent_color", "Accent colour"]].map(([k, l]) => (
              <div key={k}>
                <Label className="text-xs">{l}</Label>
                <div className="flex items-center gap-2 mt-1">
                  <input type="color" value={HEX.test(f[k]) ? f[k] : "#1E40AF"} data-testid={`wiz-${k}`}
                    onChange={(e) => set(k)(e.target.value)} className="h-9 w-12 rounded border border-slate-200 p-0.5" />
                  <Input value={f[k]} onChange={(e) => set(k)(e.target.value)} className="h-9 w-36 font-mono text-sm" />
                </div>
              </div>
            ))}
            <div><Label className="text-xs">Base application fee (₹, before GST)</Label>
              <Input className="mt-1 w-40" type="number" min={0} data-testid="wiz-fee" value={f.base_application_fee}
                onChange={(e) => set("base_application_fee")(e.target.value)} /></div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            <div><Label className="text-xs">Admin name</Label>
              <Input className="mt-1" data-testid="wiz-admin-name" value={f.initial_admin_name} onChange={(e) => set("initial_admin_name")(e.target.value)} /></div>
            <div><Label className="text-xs">Admin login email</Label>
              <Input className="mt-1" type="email" data-testid="wiz-admin-email" value={f.initial_admin_email} onChange={(e) => set("initial_admin_email")(e.target.value)} placeholder="admin@zenith.edu" /></div>
            <div><Label className="text-xs">Initial password (≥ 8 chars)</Label>
              <Input className="mt-1" type="text" data-testid="wiz-admin-pw" value={f.initial_admin_password} onChange={(e) => set("initial_admin_password")(e.target.value)} /></div>
          </div>
        )}

        <DialogFooter className="flex-row justify-between sm:justify-between">
          <Button variant="ghost" disabled={step === 0 || busy} onClick={() => setStep((s) => s - 1)}>Back</Button>
          {step < 2 ? (
            <Button className="bg-brand" data-testid="wiz-next" disabled={!canNext[step]} onClick={() => setStep((s) => s + 1)}>Next</Button>
          ) : (
            <Button className="bg-brand" data-testid="wiz-submit" disabled={busy || !canNext[2]} onClick={submit}>
              <Check className="w-4 h-4 mr-1.5" /> {busy ? "Provisioning…" : "Create tenant"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
